import * as React from 'react';
import clsx from 'clsx';
import { Tooltip } from '@/components/ui/Tooltip';
import type { MessageRating } from '@/types/ratings';

import playIcon from '@/assets/images/svg/play_icon.svg';
import copyIcon from '@/assets/images/svg/copy_icon.svg';
import shareIcon from '@/assets/images/svg/share_icon.svg';
import rateIcon from '@/assets/images/svg/rate_icon.svg';
import badResponseIcon from '@/assets/images/svg/bad_response.svg';
import goodResponseIcon from '@/assets/images/svg/good_response.svg';
import playIconPressed from '@/assets/images/svg/play_icon_pressed.svg';
import copyIconPressed from '@/assets/images/svg/copy_icon_pressed.svg';
import shareIconPressed from '@/assets/images/svg/share_icon_pressed.svg';
import rateIconPressed from '@/assets/images/svg/rate_icon_pressed.svg';
import badResponsePressedIcon from '@/assets/images/svg/bad_response_pressed.svg';
import goodResponsePressedIcon from '@/assets/images/svg/good_response_pressed.svg';

type RatingType = 'like' | 'dislike' | 'super_like';
type TransientAction = 'copy' | 'share' | null;

interface PublicMessageActionsProps {
  content: string;
  hasAudio: boolean;
  isPlaying: boolean;
  onPlay: () => void;
}

const EMPTY_RATING: MessageRating = {
  is_liked: false,
  is_disliked: false,
  is_super_liked: false,
};

export const PublicMessageActions: React.FC<PublicMessageActionsProps> = ({
  content,
  hasAudio,
  isPlaying,
  onPlay,
}) => {
  const [rating, setRating] = React.useState<MessageRating>(EMPTY_RATING);
  const [activeAction, setActiveAction] = React.useState<TransientAction>(null);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  const showTransientState = (action: Exclude<TransientAction, null>) => {
    setActiveAction(action);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setActiveAction(null);
      timeoutRef.current = null;
    }, 1600);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      showTransientState('copy');
    } catch {
      // Clipboard can be unavailable on insecure origins; leave the UI unchanged.
    }
  };

  const handleShare = async () => {
    const shareText = `${content}\n\n${window.location.href}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'D.ECHO', text: shareText });
      } else {
        await navigator.clipboard.writeText(shareText);
      }
      showTransientState('share');
    } catch {
      // Ignore cancelled native share sheets.
    }
  };

  const handleRate = (type: RatingType) => {
    setRating((current) => {
      const isActive =
        (type === 'like' && current.is_liked) ||
        (type === 'dislike' && current.is_disliked) ||
        (type === 'super_like' && current.is_super_liked);

      if (isActive) return EMPTY_RATING;
      return {
        is_liked: type === 'like',
        is_disliked: type === 'dislike',
        is_super_liked: type === 'super_like',
      };
    });
  };

  return (
    <div className='ml-2 mt-3 flex flex-wrap items-center gap-4 xl:ml-6 xl:gap-5'>
      {hasAudio && (
        <Tooltip content={isPlaying ? 'Playing' : 'Play'} position='bottom'>
          <button
            type='button'
            onClick={onPlay}
            className='transition-transform hover:scale-110 active:scale-95'
            aria-label='Play audio'
          >
            <img src={isPlaying ? playIconPressed : playIcon} alt='' className='size-5' />
          </button>
        </Tooltip>
      )}

      <Tooltip content={activeAction === 'copy' ? 'Copied' : 'Copy'} position='bottom'>
        <button
          type='button'
          onClick={handleCopy}
          className='transition-transform hover:scale-110 active:scale-95'
          aria-label='Copy message'
        >
          <img
            src={activeAction === 'copy' ? copyIconPressed : copyIcon}
            alt=''
            className='size-5'
          />
        </button>
      </Tooltip>

      <Tooltip content={activeAction === 'share' ? 'Shared' : 'Share'} position='bottom'>
        <button
          type='button'
          onClick={handleShare}
          className='transition-transform hover:scale-110 active:scale-95'
          aria-label='Share message'
        >
          <img
            src={activeAction === 'share' ? shareIconPressed : shareIcon}
            alt=''
            className='size-5'
          />
        </button>
      </Tooltip>

      <Tooltip content={rating.is_super_liked ? 'Super liked' : 'Super like'} position='bottom'>
        <button
          type='button'
          onClick={() => handleRate('super_like')}
          className={clsx(
            'transition-transform hover:scale-110 active:scale-95',
            rating.is_super_liked ? 'opacity-100' : 'opacity-80 hover:opacity-100'
          )}
          aria-label='Super like'
        >
          <img src={rating.is_super_liked ? rateIconPressed : rateIcon} alt='' className='size-5' />
        </button>
      </Tooltip>

      <Tooltip content='Bad response' position='bottom'>
        <button
          type='button'
          onClick={() => handleRate('dislike')}
          className={clsx(
            'transition-transform hover:scale-110 active:scale-95',
            rating.is_disliked ? 'opacity-100' : 'opacity-80 hover:opacity-100'
          )}
          aria-label='Bad response'
        >
          <img
            src={rating.is_disliked ? badResponsePressedIcon : badResponseIcon}
            alt=''
            className='size-5'
          />
        </button>
      </Tooltip>

      <Tooltip content='Good response' position='bottom'>
        <button
          type='button'
          onClick={() => handleRate('like')}
          className={clsx(
            'transition-transform hover:scale-110 active:scale-95',
            rating.is_liked ? 'opacity-100' : 'opacity-80 hover:opacity-100'
          )}
          aria-label='Good response'
        >
          <img
            src={rating.is_liked ? goodResponsePressedIcon : goodResponseIcon}
            alt=''
            className='size-5'
          />
        </button>
      </Tooltip>
    </div>
  );
};
